import * as React from "react";
import styled from "@emotion/styled";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, H1, Link, Text } from "atoms";
import { Head } from "features/misc";
import { RedirectState } from "types";
import { useAuth } from "./AuthProvider";
import { ConnectWallet } from "./ConnectWallet";
import { useActiveConnector } from "../utils/useActiveConnector";
import { useWalletAddress } from "../utils/useWalletAddress";

const stateMessage = ({
  isRedirected,
  isLoading,
  isWalletConnected,
  error,
}: {
  isRedirected: boolean;
  isLoading: boolean;
  isWalletConnected: boolean;
  error: string | null;
}): string => {
  if (error) {
    return error;
  }
  if (isWalletConnected) {
    return "Waiting for signature";
  }
  if (isLoading) {
    return "Waiting...";
  }

  return !isRedirected
    ? "We'll need your wallet to get started."
    : "You must connect to your wallet to see this page.";
};

function SignIn() {
  const location = useLocation();
  const previousLocation = (location.state as RedirectState)?.from;
  const navigate = useNavigate();
  const walletAddress = useWalletAddress();
  const connector = useActiveConnector();

  const { signIn, isLoading, isError, error, user } = useAuth();

  //
  // Current State
  //
  const isWalletConnected = !!connector;
  // Base heading text off of how the user ended up here: clicking sign-in button or redirected
  const title = !previousLocation
    ? "Sign in to The HUG"
    : "Connect Your Wallet";
  const message = stateMessage({
    isRedirected: !!previousLocation,
    isLoading,
    isWalletConnected,
    error,
  });

  //
  // Step 1 - Wallet Connect => Verify NFT Ownership
  //
  React.useEffect(() => {
    if (connector && !user && !isError) {
      signIn(connector, walletAddress);
    }
  }, [signIn, walletAddress, connector, user, isError]);

  // Step 2 - User is signed in, redirect back to where they were or to home page
  React.useEffect(() => {
    if (user) {
      navigate(previousLocation ?? "/");
    }
  }, [user, navigate, previousLocation]);

  const reload = () => navigate(0);

  // Need Wallet
  return (
    <Main>
      <Head title="Sign In" />

      <Header>
        <H1>{title}</H1>
        <Text size="lg">
          {message}
          <br />
          <Text as="span" size="xs">
            We need to check your HUG Pass to verify your access level.
          </Text>
        </Text>
      </Header>

      {!isWalletConnected && <ConnectWallet />}

      {isError && (
        <Button variant="gradient" onClick={reload}>
          Reload
        </Button>
      )}
      <Text size="xs">
        <br />
        By connecting your wallet, you agree to our{" "}
        <Link to="/terms-of-service">Terms of Service</Link> and{" "}
        <Link to="/privacy-policy">Privacy Policy</Link>.
      </Text>
    </Main>
  );
}

const Main = styled.main({ textAlign: "center" });

const Header = styled.header({ maxWidth: 600 });

export { SignIn };
